/**
 * 语音播报。
 *
 * 用浏览器自带的语音合成（Web Speech API），不装任何东西、不出网。
 *
 * 三个现实约束（门店环境实测出来的）：
 *   1. 店里 75~85 分贝，语音必须短、慢、重复关键信息（「请到取餐口取餐」）。
 *   2. 有些触屏机/内嵌浏览器根本没有语音合成能力，或者没有中文语音包。
 *      所以**不能把「播报成功」当成流程的一环** —— 它只是锦上添花，
 *      屏幕上的大字取餐号才是必须做对的事。
 *   3. 语音播报失败绝不能影响出单，所以这里全部 try/catch 吞掉。
 */

export interface SpeakOptions {
  /** 语速，默认 0.9（比正常慢一点，嘈杂环境更听得清） */
  rate?: number;
  /** 音量 0~1 */
  volume?: number;
}

/** 本机是否具备语音播报能力 */
export function isVoiceAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined' && typeof window.SpeechSynthesisUtterance === 'function';
}

/**
 * 播报一句话。
 * @returns 是否真的发起了一次播报（调用方只用于日志，不用于流程判断）
 */
export function speak(text: string, options: SpeakOptions = {}): boolean {
  if (!text || !isVoiceAvailable()) return false;
  try {
    const synth = window.speechSynthesis;
    // 先清空队列：上一位顾客的「取餐号」还没播完就被下一位听到，是最糟糕的体验
    synth.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'zh-CN';
    utterance.rate = options.rate ?? 0.9;
    utterance.volume = options.volume ?? 1;
    synth.speak(utterance);
    return true;
  } catch {
    // 播报失败不影响出餐，静默降级
    return false;
  }
}

/** 取餐号的播报词（现场听惯的那一句，别乱改） */
export function pickupAnnouncement(pickupNo: string | null, orderNo: string): string {
  const label = pickupNo && pickupNo.trim() !== '' ? pickupNo : orderNo;
  return `取餐号 ${label}，请到取餐口取餐`;
}
